/**
 * useShowSystemEvents — Phase 6.3.1 toggle hook.
 *
 * Persists the "show continuous_injector system events" preference to
 * localStorage so the user's choice survives reloads. Single-tab assumption
 * — cross-tab `storage` event sync is OUT OF SCOPE for Phase 6.3.1 (per the
 * plan §2 Decision 3 lock).
 *
 * Default is OFF (system events hidden) so the Recent Ingests widget stays
 * focused on user-initiated text/url/repo/image work. User opts in via the
 * toggle button in the widget header.
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'vibe_designer_show_system_events';

function readInitial(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === 'true';
  } catch {
    return false;
  }
}

export function useShowSystemEvents(): [boolean, (v: boolean) => void] {
  const [showSystemEvents, setShowState] = useState<boolean>(readInitial);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, showSystemEvents ? 'true' : 'false');
    } catch {
      // Best-effort persistence; the in-memory state is still correct.
    }
  }, [showSystemEvents]);

  const setShowSystemEvents = useCallback((v: boolean) => {
    setShowState(v);
  }, []);

  return [showSystemEvents, setShowSystemEvents];
}
